import React from "react";
import { useDashboardStats } from "../../../hooks/useAdminDashboard";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];

const STAT_COLORS = {
  red: "bg-red-600",
  blue: "bg-blue-600",
  green: "bg-green-600",
};

const StatCard = ({ title, value, icon, color }) => (
  <div className={`stat shadow ${STAT_COLORS[color] || "bg-gray-600"} text-white rounded-lg`}>
    <div className="stat-figure text-3xl">
      <i className={`fa-solid ${icon}`}></i>
    </div>
    <div className="stat-title text-white">{title}</div>
    <div className="stat-value">{value ?? "—"}</div>
  </div>
);

const BADGE_COLORS = {
  success: "badge-success",
  error: "badge-error",
  info: "badge-info",
  ghost: "badge-ghost",
  warning: "badge-warning",
};

const StatusRow = ({ label, count, color }) => (
  <div className="flex justify-between items-center py-1">
    <span className="text-sm">{label}</span>
    <span className={`badge ${BADGE_COLORS[color] || "badge-ghost"} badge-sm`}>{count ?? 0}</span>
  </div>
);

const AdminDashboard = () => {
  const { data, isLoading, error } = useDashboardStats();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="skeleton h-28 rounded-lg"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Failed to load dashboard stats: {error.message}</span>
      </div>
    );
  }

  const stats = data?.data;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Dashboard</h1>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Registered Donors"
          value={stats?.donors?.total}
          icon="fa-user-check"
          color="blue-600"
        />
        <StatCard
          title="Public Donors"
          value={stats?.publicDonors?.total}
          icon="fa-users"
          color="green-600"
        />
        <StatCard
          title="Blood Requests"
          value={stats?.bloodRequests?.total}
          icon="fa-hand-holding-droplet"
          color="red-600"
        />
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Donor Status */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Donor Status</h2>
            <StatusRow
              label="Active"
              count={stats?.donors?.byStatus?.active}
              color="success"
            />
            <StatusRow
              label="Inactive"
              count={stats?.donors?.byStatus?.inactive}
              color="error"
            />
          </div>
        </div>

        {/* Public Donor Status */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Public Donor Status</h2>
            <StatusRow
              label="Active"
              count={stats?.publicDonors?.byStatus?.active}
              color="success"
            />
            <StatusRow
              label="Inactive"
              count={stats?.publicDonors?.byStatus?.inactive}
              color="error"
            />
          </div>
        </div>

        {/* Request Status */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <h2 className="card-title text-sm">Request Status</h2>
            <StatusRow
              label="Open"
              count={stats?.bloodRequests?.byStatus?.open}
              color="info"
            />
            <StatusRow
              label="Fulfilled"
              count={stats?.bloodRequests?.byStatus?.fulfilled}
              color="success"
            />
            <StatusRow
              label="Cancelled"
              count={stats?.bloodRequests?.byStatus?.cancelled}
              color="ghost"
            />
            <StatusRow
              label="Closed"
              count={stats?.bloodRequests?.byStatus?.closed}
              color="warning"
            />
          </div>
        </div>
      </div>

      {/* Blood Group Distribution */}
      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title">Blood Group Distribution</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {BLOOD_GROUPS.map((group) => {
              const publicCount = stats?.publicDonors?.byGroup?.[group] || 0;
              const requestCount = stats?.bloodRequests?.byGroup?.[group] || 0;
              const total = publicCount + requestCount;

              return (
                <div key={group} className="text-center p-3 bg-base-200 rounded-lg">
                  <div className="text-lg font-bold text-red-600">{group}</div>
                  <div className="text-xs space-y-1 mt-2">
                    <div>
                      Donors: <span className="font-semibold">{publicCount}</span>
                    </div>
                    <div>
                      Requests: <span className="font-semibold">{requestCount}</span>
                    </div>
                  </div>
                  <progress
                    className="progress progress-error mt-2"
                    value={total}
                    max={Math.max(total, 1)}
                  ></progress>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
